import React, { useState, useEffect, useRef } from "react";
import { FaCog } from "react-icons/fa";
import { MdOutlineNoteAlt } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { IoSearch } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";
import Loading from "../components/Loading";
import { useAdmin } from "../contexts/AdminContext";

const CustomDropdown = ({ label, options, selectedValue, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleOptionClick = (option) => {
    onSelect(option);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div
      className="custom-dropdown"
      ref={dropdownRef}
      style={{
        width: 160,
        margin: 0,
      }}
    >
      <button
        type="button"
        className="dropdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid gray",
          borderRadius: 5,
        }}
      >
        {selectedValue || label}
      </button>
      {isOpen && (
        <ul className="dropdown-options">
          {options.map((option) => (
            <li key={option} onClick={() => handleOptionClick(option)}>
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const BookingManagement = () => {
  const { admin } = useAdmin();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedDay, setSelectedDay] = useState("All Days");
  const [selectedSchedule, setSelectedSchedule] = useState("All Time");
  const [slots, setSlots] = useState(["All Time"]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/booking/${admin?.branch}`);
      if (!response.ok) {
        throw new Error("Failed to fetch booking");
      }
      const data = await response.json();
      const sortedBookings = data.bookings.sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      });
      setBookings(sortedBookings);
      setFilteredBookings(sortedBookings);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching booking:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/booking/${bookingId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete booking.");
      }

      const data = await response.json();
      alert("Booking deleted successfully!");
      fetchBookings();
    } catch (error) {
      console.error("An error occurred while deleting the booking.");
    }
  };

  const fetchSlots = async () => {
    try {
      const currentDate = new Date().toISOString().split("T")[0];
      const response = await fetch(
        `${API_URL}/slot/${admin?.branch}/${currentDate}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch slots");
      }
      const data = await response.json();
      const times = data.map((slot) => slot.time);
      setSlots(["All Time", ...times]);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchSlots();
  }, []);

  const filterBookings = () => {
    let filtered = bookings;

    if (selectedDay && selectedDay !== "All Days") {
      const selectedDate = new Date();
      if (selectedDay === "Yesterday") {
        selectedDate.setDate(selectedDate.getDate() - 1);
      }
      const selectedDateString = selectedDate.toISOString().split("T")[0];
      filtered = filtered.filter(
        (booking) => booking.date.split("T")[0] === selectedDateString
      );
    }

    if (selectedSchedule && selectedSchedule !== "All Time") {
      filtered = filtered.filter(
        (booking) => booking.slotTime === selectedSchedule
      );
    }

    if (search) {
      filtered = filtered.filter(
        (booking) =>
          booking.memberId.includes(search) ||
          booking.memberName.toLowerCase().includes(search.toLowerCase()) ||
          booking.phoneNo.includes(search) ||
          booking.slotDay.toLowerCase().includes(search.toLowerCase())
      );
    }

    return filtered;
  };

  const handleSearch = () => {
    const filtered = filterBookings();
    setFilteredBookings(filtered);
  };

  return (
    <div className="userManagementContainer">
      <div className="userManagementHeader">
        <div className="userHeaderLeft">
          <MdOutlineNoteAlt
            size={30}
            color="#ff0000"
            style={{ marginRight: 10 }}
          />
          <h2>Booking Management</h2>
        </div>
        <CustomDropdown
          label="Select Day"
          options={["All Days", "Today", "Yesterday"]}
          selectedValue={selectedDay}
          onSelect={setSelectedDay}
        />
        <CustomDropdown
          label="Select Schedule"
          options={slots}
          selectedValue={selectedSchedule}
          onSelect={setSelectedSchedule}
        />
        <div className="userHeaderRight" style={{ width: "24%" }}>
          <input
            placeholder="Search by member Id or member name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div onClick={handleSearch}>
            <IoSearch />
          </div>
        </div>
        <div
          className="bookingSettingsTag"
          onClick={() => navigate("/admin/bookingmanagement/settings")}
        >
          <FaCog />
          <p>Settings</p>
        </div>
      </div>
      <h3>Booking History:</h3>
      <div className="table-container">
        {loading ? (
          <Loading />
        ) : (
          <table className="custom-table">
            <thead>
              <tr>
                <th>Member ID</th>
                <th>Member Name</th>
                <th>PhoneNo</th>
                <th>Days</th>
                <th>Time</th>
                <th>Delete</th>
              </tr>
            </thead>

            <tbody>
              {filteredBookings.map((booking) => (
                <tr key={booking.memberId}>
                  <td>{booking?.memberId}</td>
                  <td>{booking?.memberName}</td>
                  <td>{booking?.phoneNo}</td>
                  <td>{booking?.slotDay}</td>
                  <td>{booking?.slotTime}</td>
                  <td
                    className="deleteBtnGymTable"
                    onClick={() => handleDelete(booking._id)}
                  >
                    <RiDeleteBin6Line style={{ cursor: "pointer" }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BookingManagement;
